import { useCallback, useRef } from "react";
import { message } from "antd";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import db from "../../../../database";
import { SyncData } from "../Tables-Management/SyncData";
import { kotPrinter } from "../../../../lib/printer/kotPrinter";
import CWKOTPrinter from "../../../../lib/printer/CWKotPrinter";
import { numberToWords } from "../../../../utils/numberToWords";
import useCartStore from "../../../../stores/useCartStore";
import useSessionStore from "../../../../stores/useSessionStore";
import useRestaurantStore from "../../../../stores/useRestaurantStore";
import { fetchTablesStatusData, getFBOrderTableData } from "../../../../services/syncServices";
import { resolveSequenceType, generateDocumentNo } from "../../../../utils/documentSequenceUtils";
import { markLocalFire } from "../../../../utils/localFireTracker";
import { refreshLocalOrdersBeforeReconcile, isTableGenuinelyOrphaned } from "../../../../utils/tableStatusReconciliation";
import { isCompletedOrderExpired } from "../../../../utils/completedOrderRetention";

/**
 * Provides order lifecycle handlers for the Restaurant POS:
 * handleNewOrder, handleExistingOrder, cleanupCompletedOrders,
 * handleTable, handleOrder, handleDisplayOk, handleCancelOrder,
 * getDailyGlobalTokenKey
 */
const useOrderHandlers = ({
  cart,
  setCart,
  selectedTable,
  setSelectedTable,
  setTableCards,
  setOcupiedTablesData,
  setReservedTablesData,
  setTableList,
  setOederedData,
  setPendingFireItems,
  setSelectedItemsModalOpen,
  setShowProducts,
  setKeyValue,
  clearCartData,
  documentSequence,
  setDocumnetSequence,
  pendingFireItems,
  fireMode,
  posConfig,
  tillData,
  userPreferences,
  precision,
  CWHardwareController,
  history,
  guestForm,
  setGuestMobile,
  setInputFields,
  setPriorityBtn,
  setRecardState,
  setShowSendToKitchen,
  inputRef,
  setGuestsModalFlag,
  setSplitBillScreen,
  setNumSplits,
  setIsSplitBill,
  setCurrentSplitIndex,
  setSplitCarts,
}) => {
  // In-flight guard for handleDisplayOk (KOT fire submit). A ref, not state —
  // state updates are async, so two invocations landing in the same tick both
  // read the stale "not firing" value before either write lands. This is the
  // gap BUG-1993/1997 fell through: the Confirm button in RestaurantModals.js
  // had no guard of its own, and the "Fire All to Kitchen" guard
  // (CartOrderSidebar.js, added under BUG-1499/6ac113cc) only wraps the
  // synchronous handleOrder() that opens the confirmation modal — not the
  // actual async submit, which happens one step later on a different button.
  // Guarding here instead of only in the UI covers every caller, including
  // the direct (non-button) call in Restaurant/index.js on table auto-open.
  const isFiringRef = useRef(false);

  function getDailyGlobalTokenKey() {
    const today = new Date().toISOString().split("T")[0];
    return `global_daily_kot_token_counter_${today}`;
  }

  const handleNewOrder = async (tableData, newCart, userPref) => {
    // BUG (table not cleared after paying directly off Prepare Bill, no reopen in
    // between): unlike handleTable's reopen path (which stamps tableId on every cart
    // rebuild branch — see applyPreparedBillOverride's caller above and the tableId:
    // assignments below), a table opened for the FIRST time this session never got
    // tableId stamped onto its cart at all. Fire/Prepare Bill/Pay Bill then all ran
    // against a cart with no tableId, so the completion cleanup in
    // processOrderApproved (useOrderProcessing.js) had no reliable per-order table
    // reference to fall back on. Stamping it here, at cart creation, closes that gap
    // at the source instead of only at reopen time.
    const cartWithTableId = { ...newCart, tableId: tableData.cwrFbTableId };
    const updatedResponse = {
      ...tableData,
      fbOrderId: uuidv4().replace(/-/g, "").toUpperCase(),
      lines: [],
      items: [],
      ordered: "Y",
      fbOrderSync: "N",
      fbOrderStatus: "IP",
      dineIn: useRestaurantStore.getState().dineIn,
      createdTime: dayjs(new Date()).format(userPref.dateTimeFormat),
      cart: cartWithTableId,
      splitTable: tableData?.splitTable || null,
    };

    await db.tableData.update(tableData.cwrFbTableId, {
      ordered: "Y",
      documentno: newCart.documentno,
      statusType: tableData.statusType || "OCU",
      color: "#a7c957",
    });

    await SyncData(updatedResponse, "upsertTableStatus");
    setCart(updatedResponse.cart || cartWithTableId);
  };

  // TSK-2935: fbOrderData's cart/wsData snapshot is written at KOT-fire time and never
  // updated by Prepare Bill (which only stamps the live useCartStore cart). Reselecting
  // this table (e.g. cashier serves another table, then returns) rebuilds the cart from
  // that stale snapshot below, silently reverting the real, server-confirmed document
  // number Prepare Bill already obtained back to whatever preview was showing at
  // KOT-fire time — and DI-33's stale-identity guard then treats the mismatch as a
  // different order and regenerates sOrderID, permanently severing the link (the exact
  // bug TSK-2935 exists to fix). If this table's order already has a real document
  // number from Prepare Bill, restore it onto the rebuilt cart instead of trusting the
  // snapshot.
  const applyPreparedBillOverride = async (cartData) => {
    if (!cartData?.sOrderID) return cartData;
    try {
      const existingOrder = await db.orders.get(cartData.sOrderID);
      if (existingOrder?.preparedBillOrigin === "Y") {
        return { ...cartData, documentno: existingOrder.documentno, preparedBillOrigin: "Y" };
      }
    } catch {
      // best-effort — fall back to the snapshot's own documentno on failure
    }
    return cartData;
  };

  const handleExistingOrder = async (selTable) => {
    const tableRecord = await db.tableData.get(selTable?.cwrFbTableId);
    if (selTable?.splitTable) {
      console.debug("⛔ handleExistingOrder skipped for split table:", selTable.splitTable);
      return;
    }

    const orders = await db.fbOrderData.where("cwrFbTableId").equals(tableRecord.cwrFbTableId).toArray();
    const ordersData = orders.find((order) => order.fbOrderStatus === "IP");
    const ordersFetched = ordersData ? [ordersData] : [];

    // [BUG-1749] statusType ONG/OCU/BRQ all mean "an order is in progress on
    // this table" — TableLayout renders every one of them as "ORDER PLACED".
    // If no backing fbOrderData IP row actually exists, the table is an
    // orphaned/stale record (e.g. left behind by an interrupted flow or a
    // stale-order cleanup that only deleted the order row) — reconcile it
    // back to Available instead of silently leaving the bad status in place.
    // STD (Seated, guest checked in but hasn't ordered yet) is intentionally
    // excluded — that is the correct, expected "no order yet" state.
    //
    // [Table Status Data Mismatch] The original check here only looked at the
    // LOCAL fbOrderData cache, so a table whose order simply hadn't synced to
    // this terminal yet (mesh/cloud lag) was indistinguishable from a truly
    // orphaned one — and this fires on every table tap, so it destructively
    // wiped a genuinely running table to Available on click. Ask the server
    // directly instead, bounded to ~1.8s so tapping a table never hangs; a
    // timeout/offline/error result means "don't know" and must not wipe.
    if (ordersFetched.length === 0 && ["ONG", "OCU", "BRQ", "FSM"].includes(tableRecord.statusType)) {
      const confirmedOrphaned = await isTableGenuinelyOrphaned(tableRecord.cwrFbTableId);
      if (confirmedOrphaned === true) {
        await db.tableData.update(tableRecord.cwrFbTableId, {
          statusType: "OPN",
          ordered: "N",
          color: "rgba(146, 144, 152, 0.5)",
          wsData: {},
          items: [],
          guestName: "",
          noOfPersons: 0,
        });
      }
    }

    if (ordersFetched.length > 0) {
      ordersFetched.forEach(async (fbOrder) => {
        if (fbOrder?.wsData && typeof fbOrder?.wsData === "object" && Object.keys(fbOrder?.wsData).length > 0 && fbOrder?.fbOrderStatus === "IP") {
          if (fbOrder?.wsData?.items?.length > 0) {
            const activeWsItems = fbOrder?.wsData?.items.filter(item => item.isCanceled !== "Y") || [];
            const total = activeWsItems.reduce((sum, item) => sum + (Number(item.nettotal) || 0), 0);
            const tax = activeWsItems.reduce((sum, item) => sum + (Number(item.taxAmount) || 0), 0);
            const totalQty = activeWsItems.reduce((sum, item) => sum + (Number(item.weight) || 0), 0);
            const discount = activeWsItems.reduce((sum, item) => sum + (Number(item.discount) || 0), 0);
            const preciseTotal = parseFloat(cart?.total?.toFixed(precision));
            let cartData = {
              ...fbOrder.wsData,
              tip: fbOrder.cart?.tip || fbOrder.wsData?.tip || 0,
              serviceCharge: fbOrder.cart?.serviceCharge || fbOrder.wsData?.serviceCharge || 0,
              deliveryCharge: fbOrder.cart?.deliveryCharge || fbOrder.wsData?.deliveryCharge || 0,
              deiveryCharge: fbOrder.cart?.deiveryCharge || fbOrder.wsData?.deiveryCharge || 0,
              packingCharge: fbOrder.cart?.packingCharge || fbOrder.wsData?.packingCharge || 0,
              tableNo: selTable?.name,
              items: fbOrder.wsData.items?.map((line) => ({
                ...line,
                sendQty: line.order === "Y" ? 0 : Math.max((Number(line.weight) || 0) - (Number(line.firedQty) || 0), 0),
                parkedItem: true,
              })),
              totalQty,
              total: parseFloat(total.toFixed(precision)),
              tax: parseFloat(tax.toFixed(precision)),
              discount: parseFloat(discount.toFixed(precision)),
              subTotal: parseFloat(parseFloat(total.toFixed(precision)) - parseFloat(tax.toFixed(precision))),
              totalStd: cart?.items?.reduce((sum, item) => (item?.isCanceled === "Y" ? sum : sum + (item?.linestd || 0)), 0) || 0,
              amountInWords: numberToWords(preciseTotal),
              paid: 0,
              payments: [],
            };
            // BUG-2338 (RC3): a table reopen never went through selectLayAwayOrder/
            // selectParkedBill (Core/hooks/useParkedBills.js) — those are the only 5
            // places that stamp retrievedDocumentno (see cartIdentityGuard.js). A
            // dine-in order with docType:"Advance"/layAway:"Y" reopened via a table
            // tap therefore always hit the DI-33 guard's broken empty-preorderPrefix
            // fallback on completion/cancel, unconditionally regenerating sOrderID.
            // Stamp the same snapshot here — AFTER applyPreparedBillOverride, since
            // that can itself override documentno to the server-confirmed one
            // (TSK-2935); stamping before it would snapshot a value this same
            // function immediately replaces, defeating the fix on Prepare-Bill tables.
            const overriddenCartData = await applyPreparedBillOverride(cartData);
            overriddenCartData.retrievedDocumentno = overriddenCartData.documentno;
            setCart(overriddenCartData);
          }
        } else if (fbOrder?.wsData && typeof fbOrder?.wsData === "object" && Object.keys(fbOrder?.wsData).length === 0) {
          let cartObj = useCartStore.getState().cart;
          let cartData = { ...cartObj, items: [] };
          setCart(cartData);
        } else {
          if (fbOrder.fbOrderStatus === "IP") {
            const activeCartItems = fbOrder.cart.items.filter(item => item.isCanceled !== "Y");
            const total = activeCartItems.reduce((sum, item) => sum + (Number(item.nettotal) || 0), 0);
            const tax = activeCartItems.reduce((sum, item) => sum + (Number(item.taxAmount) || 0), 0);
            const totalQty = activeCartItems.reduce((sum, item) => sum + (Number(item.weight) || 0), 0);
            // [BUG-1609] fbOrder.cart.discount is a cached top-level field that can go stale
            // (e.g. a KOT re-fire or reopen persisting a cart snapshot from before a discount
            // was applied). total/tax/totalQty just above are already recomputed from
            // activeCartItems for the same reason — discount must be too, matching the sibling
            // wsData branch above (line ~111) which already recomputes it this way.
            const discount = activeCartItems.reduce((sum, item) => sum + (Number(item.discount) || 0), 0);
            let cartData = {
              ...fbOrder.cart,
              discount: parseFloat(discount.toFixed(precision)),
              orderTime: fbOrder.creatdeTime,
              tableNo: fbOrder.name,
              statusType: "OCU",
              sOrderID: fbOrder?.cart?.sOrderID || uuidv4().replace(/-/g, "").toUpperCase(),
              items: fbOrder.cart.items.map((item) => ({ ...item, sendQty: 0 })),
              totalQty,
              total: parseFloat(total.toFixed(precision)),
              tax: parseFloat(tax.toFixed(precision)),
              paid: 0,
              payments: [],
            };
            // BUG-2338 (RC3): see the matching comment in the wsData branch above —
            // same gap and same applyPreparedBillOverride-ordering fix, this branch's
            // snapshot source is fbOrder.cart instead.
            const overriddenCartData = await applyPreparedBillOverride(cartData);
            overriddenCartData.retrievedDocumentno = overriddenCartData.documentno;
            setCart(overriddenCartData);
          }
        }
      });
    }
  };

  const cleanupCompletedOrders = async () => {
    // Delete ALL completed orders — fbOrderData is for table display state only,
    // order data is already persisted in db.orders. Previously only deleted CO+synced records,
    // but processOrderApproved sets fbOrderSync:"N", so completed orders were never cleaned up.
    const today = new Date().toISOString().split("T")[0];
    const completedOrders = await db.fbOrderData.where("fbOrderStatus").equals("CO").toArray();
    // BUG-2357: keep TODAY's completed orders. Deleting them right away let
    // getFBOrdersData() (refreshLocalOrdersBeforeReconcile below) re-insert an order the
    // server still had open as "IP", and the next customer's KOT was appended to it —
    // showing the previous bill's items on the table. A kept row makes insertFbOrdersToDB
    // skip that fbOrderId and lets the existing stale-IP guards (useWebSocketHandler
    // ORDER_UPSERT, handleTable's isStaleServerState) ignore it / re-send CO.
    const expiredCompleted = completedOrders.filter((o) => isCompletedOrderExpired(o, today));
    const affectedTableIds = new Set(expiredCompleted.map((o) => o.cwrFbTableId).filter(Boolean));
    for (const order of expiredCompleted) {
      await db.fbOrderData.where("id").equals(order.id).delete();
    }

    // toArray + JS filter: a .filter() closure is serialized via toString() over
    // the SQLite IPC bridge and loses the captured `today`, silently matching nothing.
    // Split orders are included: with the hasActiveOrdersForTable guards, an
    // abandoned split IP row from a previous day would otherwise keep its table
    // permanently occupied with no cleanup path.
    const allFbOrders = await db.fbOrderData.toArray();
    const staleIpOrders = allFbOrders.filter((o) => o.fbOrderStatus === "IP" && o.updatedTime && !o.updatedTime.startsWith(today));
    for (const order of staleIpOrders) {
      affectedTableIds.add(order.cwrFbTableId);
      await db.fbOrderData.where("id").equals(order.id).delete();
    }

    // [BUG-1749] Deleting an order row here never reconciled the table's
    // statusType — a table left at ONG/OCU/BRQ with no order backing it
    // anymore showed "Order Placed" (TableLayout renders all three that way)
    // with zero items until something else happened to touch it. Reset any
    // table whose last active order was just removed above. Split tables are
    // safe: tablesStillActive is keyed by cwrFbTableId, so a table with a
    // surviving split sibling order is skipped.
    //
    // [Table Status Data Mismatch] This runs on every table tap (called right
    // after handleExistingOrder in Restaurant/index.js) purely off the local
    // fbOrderData cache — a table whose real order hasn't synced to this
    // terminal yet looked identical to one that's genuinely done, so this
    // could also destructively wipe a still-running table. Give the local
    // cache a bounded chance to catch up from the server first.
    if (affectedTableIds.size > 0) {
      // Shorter bound than the mount/reload site's default — this only runs
      // when a table was actually just affected (not on every tap), but it's
      // still on the interactive table-selection path, so keep it snappy.
      await refreshLocalOrdersBeforeReconcile(1800);
      const remainingIpOrders = await db.fbOrderData.where("fbOrderStatus").equals("IP").toArray();
      const tablesStillActive = new Set(remainingIpOrders.map((o) => o.cwrFbTableId));
      for (const tableId of affectedTableIds) {
        if (!tableId || tablesStillActive.has(tableId)) continue;
        const table = await db.tableData.get(tableId);
        if (table && ["ONG", "OCU", "BRQ", "FSM"].includes(table.statusType)) {
          await db.tableData.update(tableId, {
            statusType: "OPN",
            ordered: "N",
            color: "rgba(146, 144, 152, 0.5)",
            wsData: {},
            items: [],
            guestName: "",
            noOfPersons: 0,
          });
        }
      }
    }
  };

  const handleOrder = (fireItems = []) => {
    console.debug("HANDLE ORDER CALLED - fireItems:", fireItems);

    // TSK-3303: NOTE — handleOrder is also invoked from the Pay Bill button's own
    // onClick (CartOrderSidebar.js) for standard non-parked dine-in tables, so it
    // must NOT be gated here — order completion must always remain possible.
    // The two actual "Fire" entry points (single-item icon, Fire All to Kitchen)
    // are gated at their own onClick in CartOrderSidebar.js instead.

    let cartObj = useCartStore.getState().cart;
    const currentCart = structuredClone(cartObj);
    const previousItems = structuredClone(cartObj.items);

    const processedItems = currentCart.items.map((item) => {
      const oldItem = previousItems.find((i) => i.lineId === item.lineId);
      const oldFired = Number(oldItem?.firedQty || 0);
      const currentQty = Number(item.weight || 0);

      console.debug(`Item: ${item.name} | item.lineId} → oldFired: ${oldFired}, current: ${currentQty}`);

      if (currentQty > oldFired) {
        const qtyToSend = currentQty - oldFired;
        console.debug(`→ WILL FIRE +${qtyToSend}`);
        return {
          ...item,
          order: "N",
          sendQty: qtyToSend,
          firedQty: currentQty,
          saleType: item.saleType || "DI",
        };
      }
      return item;
    });

    let itemsToShow = processedItems.filter((item) => item.order === "N" && item.sendQty > 0 && !item.isHold);

    if (fireItems.length > 0) {
      const ids = fireItems.map((i) => i.lineId);
      itemsToShow = itemsToShow.filter((item) => ids.includes(item.lineId));
    }

    if (itemsToShow.length === 0) {
      console.debug("Nothing to fire");
      message.info("No new items or quantity to send to kitchen");
      return;
    }

    setOederedData(itemsToShow);
    setPendingFireItems(itemsToShow);
    setCart({ ...cart, items: processedItems });
    setSelectedItemsModalOpen(true);
  };

  const handleTable = async (record) => {
    const tableRecord = await db.tableData.get(record?.cwrFbTableId);

    if (!tableRecord) {
      console.debug("undefined 'tableRecord'");
      return;
    }

    let selectedTableRecord = tableRecord;
    if (posConfig?.disableOfflineBilling === "Y") {
      const tableStatusdata = await fetchTablesStatusData(record?.cwrFbTableId);
      if (tableStatusdata?.length > 0) {
        if (tableStatusdata[0]?.orderId && tableStatusdata[0]?.orderStatus) {
          const tableOrderData = await getFBOrderTableData(record?.cwrFbTableId, tableStatusdata[0]?.orderStatus);
          if (!tableOrderData?.length) return;
          const order = tableOrderData[0];
          const now = new Date().toISOString();
          const existingOrder = await db.fbOrderData.where("fbOrderId").equals(order.fbOrderId).first();
          const isStaleServerState = existingOrder && (existingOrder.fbOrderStatus === "CL" || existingOrder.fbOrderStatus === "CO") && order.status === "IP";
          if (isStaleServerState) {
            console.debug("[handleTable] Server has stale IP for locally", existingOrder.fbOrderStatus, "order — re-syncing:", order.fbOrderId);
            SyncData(existingOrder, "upsertFbOrder");
            await db.tableData.update(record.cwrFbTableId, { wsData: {}, items: [], statusType: "OPN" });
            selectedTableRecord.statusType = "OPN";
            selectedTableRecord.wsData = {};
            selectedTableRecord.items = [];
          } else {
          const cartMetas = order.meta?.filter((m) => m.key === "cartDetails") || [];
          let cartMeta = null;
          if (cartMetas.length === 1) {
            cartMeta = cartMetas[0];
          } else if (cartMetas.length > 1) {
            // Multiple snapshots exist — pick the one whose items best match
            // the server's lines[] (ground truth for what's on this order).
            // Score: +1 per matching fbOrderLineId, +1 if isCanceled also matches.
            // This handles cases where all snapshots have the same lineIds but differ
            // in cancellation state (e.g. an item was cancelled after the first snapshot).
            const orderLineMap = new Map((order.lines || []).filter((l) => l.fbOrderLineId).map((l) => [l.fbOrderLineId, l.isCanceled || "N"]));
            let bestScore = -1;
            for (let i = 0; i < cartMetas.length; i++) {
              const meta = cartMetas[i];
              try {
                const parsed = JSON.parse(meta.value);
                let score = 0;
                const scoredFbOrderLineIds = new Set();
                for (const item of parsed.items || []) {
                  if (!item.fbOrderLineId) continue;
                  if (scoredFbOrderLineIds.has(item.fbOrderLineId)) continue;
                  scoredFbOrderLineIds.add(item.fbOrderLineId);
                  const lineCanceled = orderLineMap.get(item.fbOrderLineId);
                  if (lineCanceled === undefined) continue;
                  score++;
                  if ((item.isCanceled || "N") === lineCanceled) score++;
                }
                if (score >= bestScore) {
                  bestScore = score;
                  cartMeta = meta;
                }
              } catch (e) {}
            }
            if (!cartMeta) cartMeta = cartMetas[cartMetas.length - 1];
          }
          let wsData = {};
          try {
            wsData = cartMeta?.value ? JSON.parse(cartMeta.value) : {};
          } catch (e) {
            console.error("Invalid cartDetails JSON", e);
          }
          // Reconcile each item's qty from server lines[] so a stale snapshot never
          // causes the wrong quantity to display regardless of meta order.
          const lineQtyMap = new Map(
            (order.lines || []).filter((l) => l.fbOrderLineId).map((l) => [l.fbOrderLineId, { qty: l.qty, isCanceled: l.isCanceled || "N" }])
          );
          if (wsData.items?.length > 0 && lineQtyMap.size > 0) {
            const reconciledLineIds = new Set();
            wsData = {
              ...wsData,
              items: wsData.items.map((item) => {
                if (!item.fbOrderLineId) return item;
                reconciledLineIds.add(item.fbOrderLineId);
                const serverLine = lineQtyMap.get(item.fbOrderLineId);
                if (!serverLine) return item;
                const before = Number(item.weight) || 0;
                const after = serverLine.qty;
                // The reconcile above made qty authoritative from the server lines[], but the
                // cached money fields (nettotal/taxAmount/discount) still reflected the snapshot's old
                // qty. When the two diverge (e.g. the same item was added again before reopening the
                // table) the line showed the correct quantity with a stale amount — the total recompute
                // below sums item.nettotal, so a qty 5→10 line kept its ₹900 amount instead of ₹1800.
                // Rescale the money fields to the corrected qty so amount stays a deterministic function
                // of quantity. nettotal/tax/discount are all linear in qty here, so ratio-scaling is
                // exact and preserves any line-level discount/price edit. Guard before>0 to avoid
                // divide-by-zero and only touch lines whose qty actually changed.
                if (after !== before && before > 0) {
                  const ratio = after / before;
                  return {
                    ...item,
                    weight: after,
                    nettotal: parseFloat(((Number(item.nettotal) || 0) * ratio).toFixed(precision)),
                    taxAmount: parseFloat(((Number(item.taxAmount) || 0) * ratio).toFixed(precision)),
                    discount: parseFloat(((Number(item.discount) || 0) * ratio).toFixed(precision)),
                    isCanceled: serverLine.isCanceled,
                  };
                }
                return { ...item, weight: after, isCanceled: serverLine.isCanceled };
              }),
            };
            // Inject server lines missing from the meta snapshot (e.g. items added
            // from mobile after the last meta was saved, or cancelled lines the
            // snapshot never captured). Without this, those items silently vanish.
            const missingLines = (order.lines || []).filter((l) => l.fbOrderLineId && !reconciledLineIds.has(l.fbOrderLineId));
            if (missingLines.length > 0) {
              const existingItem = wsData.items[0];
              for (const ml of missingLines) {
                const metaValue = ml.meta?.find((m) => m.key === "orderLine");
                let metaParsed = {};
                try { metaParsed = metaValue?.value ? JSON.parse(metaValue.value) : {}; } catch (e) {}
                const injectedItem = {
                  ...metaParsed,
                  productId: ml.mPoductId,
                  name: ml.name || metaParsed.name || "Unknown",
                  weight: ml.qty,
                  fbOrderLineId: ml.fbOrderLineId,
                  lineId: metaParsed.lineId || ml.fbOrderLineId,
                  isCanceled: ml.isCanceled || "N",
                  order: "Y",
                  firedQty: ml.qty,
                  sendQty: 0,
                  parkedItem: true,
                  taxRate: metaParsed.taxRate || 0,
                  realPrice: metaParsed.realPrice || ml.unitPrice || 0,
                  salePrice: metaParsed.salePrice || ml.unitPrice || 0,
                  nettotal: metaParsed.nettotal || ml.lineNet || 0,
                  taxAmount: metaParsed.taxAmount || 0,
                  discount: metaParsed.discount || ml.discount || 0,
                };
                wsData.items.push(injectedItem);
              }
            }
          }
          if (wsData.items?.length > 0) {
            const seenKeys = new Set();
            const beforeCount = wsData.items.length;
            wsData = {
              ...wsData,
              items: wsData.items.filter((item) => {
                if (!item.fbOrderLineId) return true;
                const key = `${item.fbOrderLineId}_${item.lineId || ""}`;
                if (seenKeys.has(key)) {
                  console.warn("[handleTable] Duplicate item removed:", { name: item.name, fbOrderLineId: item.fbOrderLineId, lineId: item.lineId });
                  return false;
                }
                seenKeys.add(key);
                return true;
              }),
            };
            if (wsData.items.length < beforeCount) {
              console.warn(`[handleTable] Dedup removed ${beforeCount - wsData.items.length} item(s) from order`);
            }
          }
          // [BUG-1597] Preserve a POS-side manual discount across online reopen. The server
          // snapshot does not carry discounts applied after fire (the discount is stored only
          // in the local order record when applied), so re-apply per-line discount/nettotal/tax
          // by fbOrderLineId from the local record. If the discount was removed, the local record
          // has discount 0 → nothing re-applied → correct.
          {
            const localItems = existingOrder?.wsData?.items || existingOrder?.cart?.items || [];
            const localDiscByLine = new Map();
            localItems.forEach((li) => {
              if (li?.fbOrderLineId && Number(li.discount) > 0) localDiscByLine.set(li.fbOrderLineId, li);
            });
            if (localDiscByLine.size > 0 && Array.isArray(wsData.items)) {
              wsData = {
                ...wsData,
                items: wsData.items.map((item) => {
                  const li = localDiscByLine.get(item.fbOrderLineId);
                  if (!li) return item;
                  return {
                    ...item,
                    discount: li.discount,
                    nettotal: li.nettotal ?? item.nettotal,
                    taxAmount: li.taxAmount ?? item.taxAmount,
                    salePrice: li.salePrice ?? item.salePrice,
                    discountName: li.discountName ?? item.discountName,
                    mPricingruleId: li.mPricingruleId ?? item.mPricingruleId,
                  };
                }),
              };
            }
          }
          const recordData = {
            // 🔥 IMPORTANT → ensures update instead of insert
            id: existingOrder?.id,
            fbOrderId: order.fbOrderId,
            fbTableId: order.fbTableId,
            cwrFbTableId: order.fbTableId,
            fbOrderStatus: order.status,
            fbOrderSync: "Y",
            cSBunitID: order.cSBunitID,
            orderNo: order.orderNo,
            salesRepId: order.salesRepId,
            guestName: order.guestName,
            isCanceled: order.isCanceled,
            createdTime: existingOrder?.createdTime || now,
            updatedTime: now,
            cart: wsData,
            // [BUG-1609] db.fbOrderData.put() fully replaces the record — omitting wsData
            // here silently wiped it on every online reopen, forcing handleExistingOrder's
            // reload (triggered moments later by the same selectedTable change) into its
            // stale-cache fallback branch instead of the branch that recomputes discount
            // from items. wsData is the same reconciled snapshot already trusted for cart:
            // above; every other db.fbOrderData.put() call site in this codebase already
            // preserves it (useVoidHandler.js, useWebSocketHandler.js, useCartOperations.js).
            wsData,
            meta: order.meta || [],
            lines: order.lines || [],
          };
          await db.fbOrderData.put(recordData);
          // toArray + per-id update: .filter() closures capture `order` which is
          // lost over the SQLite IPC bridge (toString serialization) — the modify
          // would then silently close nothing (or worse, the wrong rows).
          {
            const sameTableOrders = await db.fbOrderData.where("cwrFbTableId").equals(order.fbTableId).toArray();
            const staleIp = sameTableOrders.filter((r) => r.fbOrderStatus === "IP" && r.fbOrderId !== order.fbOrderId && !r.splitTable);
            for (const stale of staleIp) {
              await db.fbOrderData.update(stale.id, { fbOrderStatus: "CL" });
            }
          }
          // Sync the in-memory tableRecord.wsData with the freshly selected server snapshot.
          // Without this, the stale wsData written by a previous WS event takes priority over
          // activeOrder.cart in the setCart callback below, causing already-fired items to
          // appear with firedQty:0 and re-fire on the next "Fire All", producing duplicate KOTs.
          // Guard: only overwrite when wsData has content — an empty wsData would trigger the
          // "wsData is empty object" branch (line 440) and incorrectly wipe the cart items.
          if (Object.keys(wsData).length > 0) {
            tableRecord.wsData = wsData;
          }
          selectedTableRecord.statusType = "ONG";
          }
        } else if (!tableStatusdata[0]?.orderId && !tableStatusdata[0]?.orderStatus) {
          // Server confirmed no active order on this table — close any stale local IP orders
          // so the cart loads empty when the table is opened next
          {
            const tableOrders = await db.fbOrderData.where("cwrFbTableId").equals(record.cwrFbTableId).toArray();
            const ipOrders = tableOrders.filter((r) => r.fbOrderStatus === "IP");
            for (const ip of ipOrders) {
              // updatedTime: cleanupCompletedOrders keeps completed rows until the next day by it (BUG-2357).
              await db.fbOrderData.update(ip.id, { fbOrderStatus: "CO", wsData: {}, updatedTime: new Date().toISOString() });
            }
          }
          await db.tableData.update(record.cwrFbTableId, { wsData: {}, items: [], statusType: "OPN" });
          selectedTableRecord.statusType = "OPN";
          selectedTableRecord.wsData = {};
          selectedTableRecord.items = [];
          console.debug("[handleTable] No active order on server — cleared stale local IP orders for table:", record.cwrFbTableId);
        }
      }
    }
    const splitIdentifier = record?.splitTable;
    if (splitIdentifier && tableRecord?.splitTables) {
      const splitInfo = tableRecord.splitTables.find((s) => s.splitTable === splitIdentifier);
      if (splitInfo) {
        selectedTableRecord = {
          ...tableRecord,
          displayName: splitIdentifier,
          tableName: splitIdentifier,
          name: splitIdentifier,
          splitTable: splitIdentifier,
          statusType: splitInfo.status,
          guests: splitInfo.guests,
          noOfPersons: splitInfo.guests,
          guestName: splitInfo.guestName || "",
          guestType: splitInfo.guestType || "",
          salesRepId: splitInfo.salesRepId || tableRecord.salesRepId,
        };
        console.debug("[v0] Selected split table:", selectedTableRecord);
      }
    }

    guestForm.resetFields();
    setGuestMobile("");
    setInputFields({});
    setPriorityBtn(false);
    guestForm.resetFields();
    setRecardState(selectedTableRecord);
    setSplitBillScreen(false);
    setNumSplits(2);
    setIsSplitBill(false);
    setCurrentSplitIndex(0);
    setSplitCarts([]);

    const ordersFetched = await db.fbOrderData.where("cwrFbTableId").equals(tableRecord.cwrFbTableId).toArray();

    let activeOrder = null;
    let allIpOrders = [];
    if (selectedTableRecord.splitTable) {
      activeOrder = ordersFetched.find((o) => o.fbOrderStatus === "IP" && o.splitTable === selectedTableRecord.splitTable);
      console.debug("[v0] Active order for split:", activeOrder, "splitTable:", selectedTableRecord.splitTable);
    } else {
      allIpOrders = ordersFetched.filter((o) => o.fbOrderStatus === "IP");
      activeOrder = allIpOrders[0] || null;
    }

    setCart((prev) => {
      let updatedCart = {
        ...clearCartData,
        customer: {
          name: selectedTableRecord.guestName || "",
          mobileNo: selectedTableRecord.mobile || "",
        },
        splitTable: selectedTableRecord.splitTable || null,
        tableId: selectedTableRecord.cwrFbTableId,
        tableName: selectedTableRecord.tableName,
      };
      if (record?.splitTable && Array.isArray(tableRecord.splitTables)) {
        const matchedSplit = tableRecord.splitTables.find((s) => s.splitTable === record.splitTable);
        if (matchedSplit?.cart) {
          console.debug("[v0] Loading FULL split cart for:", record.splitTable);
          const total = matchedSplit.cart.items.reduce((sum, item) => (item.isCanceled === "Y" ? sum : sum + (Number(item.nettotal) || 0)), 0);
          const tax = matchedSplit.cart.items.reduce((sum, item) => (item.isCanceled === "Y" ? sum : sum + (Number(item.taxAmount) || 0)), 0);
          const preciseTotal = parseFloat(cart?.total?.toFixed(precision));
          updatedCart = {
            ...matchedSplit.cart,
            items: matchedSplit.cart.items.map((item) => {
              const fired = Number(item.firedQty || 0);
              const qty = Number(item.weight || 0);
              return {
                ...item,
                sendQty: Math.max(qty - fired, 0),
                parkedItem: true,
                splitTable: record.splitTable,
              };
            }),
            tableNo: tableRecord.name,
            tableId: tableRecord.cwrFbTableId,
            tableName: record.splitTable,
            splitTable: record.splitTable,
            total: parseFloat(total.toFixed(precision)),
            tax: parseFloat(tax.toFixed(precision)),
            subTotal: parseFloat(parseFloat(total.toFixed(precision)) - parseFloat(tax.toFixed(precision))),
            totalStd: cart?.items?.reduce((sum, item) => (item?.isCanceled === "Y" ? sum : sum + (item?.linestd || 0)), 0) || 0,
            amountInWords: numberToWords(preciseTotal),
            paid: 0,
            payments: [],
          };
          return updatedCart;
        }
      }
      // Use activeOrder (resolved via .find()) instead of ordersFetched[0] — the array
      // is unordered so index 0 may be a stale IP record while the real CO sits at index 1,
      // which would cause completed-order items to load into the new transaction.
      if (activeOrder) {
        if (tableRecord?.wsData && typeof tableRecord?.wsData === "object" && Object.keys(tableRecord?.wsData).length > 0) {
          let wsItems = tableRecord.wsData.items || [];
          if (selectedTableRecord.splitTable) {
            wsItems = wsItems.filter((item) => item.splitTable === selectedTableRecord.splitTable);
          }
          updatedCart = {
            ...tableRecord.wsData,
            tip: activeOrder?.cart?.tip || tableRecord.wsData?.tip || 0,
            serviceCharge: activeOrder?.cart?.serviceCharge || tableRecord.wsData?.serviceCharge || 0,
            deliveryCharge: activeOrder?.cart?.deliveryCharge || tableRecord.wsData?.deliveryCharge || 0,
            deiveryCharge: activeOrder?.cart?.deiveryCharge || tableRecord.wsData?.deiveryCharge || 0,
            packingCharge: activeOrder?.cart?.packingCharge || tableRecord.wsData?.packingCharge || 0,
            tableNo: tableRecord?.name,
            // wsData snapshots may predate tableId stamping — re-stamp so the
            // settlement receipt gate can resolve this cart's table.
            tableId: selectedTableRecord.cwrFbTableId || tableRecord.cwrFbTableId,
            splitTable: selectedTableRecord.splitTable || null,
            items: wsItems.map((line) => ({
              ...line,
              sendQty: line.order === "Y" ? 0 : Math.max((Number(line.weight) || 0) - (Number(line.firedQty) || 0), 0),
              parkedItem: true,
              splitTable: selectedTableRecord.splitTable || line.splitTable || null,
            })),
          };
        } else if (tableRecord?.wsData && typeof tableRecord?.wsData === "object" && Object.keys(tableRecord?.wsData).length === 0) {
          updatedCart = { ...updatedCart, items: [] };
        } else if (activeOrder && activeOrder.cart) {
          const cartItems = activeOrder.cart.items || [];
          updatedCart = {
            ...activeOrder.cart,
            tableNo: selectedTableRecord?.name || selectedTableRecord?.tableName,
            tableId: selectedTableRecord.cwrFbTableId || tableRecord.cwrFbTableId,
            splitTable: selectedTableRecord.splitTable || null,
            items: cartItems.map((line) => ({
              ...line,
              sendQty: line.order === "Y" ? 0 : Math.max((Number(line.weight) || 0) - (Number(line.firedQty) || 0), 0),
              parkedItem: true,
              splitTable: selectedTableRecord.splitTable || line.splitTable || null,
            })),
          };
        } else {
          console.debug("[v0] No existing order or wsData - empty cart");
        }
      }
      if (allIpOrders.length > 1 && Array.isArray(updatedCart.items)) {
        const seenLineIds = new Set(updatedCart.items.map((i) => i.lineId).filter(Boolean));
        for (const otherOrder of allIpOrders.slice(1)) {
          const otherItems = otherOrder.wsData?.items || otherOrder.cart?.items || [];
          for (const item of otherItems) {
            if (item.lineId && !seenLineIds.has(item.lineId)) {
              seenLineIds.add(item.lineId);
              updatedCart.items.push({
                ...item,
                sendQty: item.order === "Y" ? 0 : Math.max((Number(item.weight) || 0) - (Number(item.firedQty) || 0), 0),
                parkedItem: true,
              });
            }
          }
        }
      }
      // Deduplicate items — overlapping wsData and cart sources can produce
      // duplicates after a WebSocket update. Two items sharing a lineId but
      // carrying DIFFERENT fbOrderLineIds are distinct server lines (e.g. same
      // product fired in separate KOT batches) and must BOTH be kept — dropping
      // one silently removes a fired item from the order and the bill.
      if (updatedCart.items && updatedCart.items.length > 0) {
        const seen = new Map();
        updatedCart.items = updatedCart.items.filter((item) => {
          if (!item.lineId) return true;
          const seenServerIds = seen.get(item.lineId);
          if (!seenServerIds) {
            seen.set(item.lineId, new Set([item.fbOrderLineId]));
            return true;
          }
          if (item.fbOrderLineId && !seenServerIds.has(item.fbOrderLineId)) {
            console.warn(`[handleTable] Same lineId ${item.lineId} with distinct fbOrderLineIds — keeping both server lines`);
            seenServerIds.add(item.fbOrderLineId);
            return true;
          }
          console.warn(`[handleTable] Dropping duplicate cart item — lineId: ${item.lineId}, product: ${item.name || item.value}`);
          return false;
        });
      }
      // Recalculate cart totals from actual items — wsData snapshots can carry
      // stale totalQty/total/tax after item reconciliation or multi-order merges.
      // Also reset paid/payments: any non-zero values in the snapshot are from an
      // abandoned payment attempt in a prior session and must not carry forward.
      if (updatedCart.items && updatedCart.items.length > 0) {
        const activeItems = updatedCart.items.filter(item => item.isCanceled !== "Y");
        updatedCart.totalQty = activeItems.reduce((sum, item) => sum + (Number(item.weight) || 0), 0);
        updatedCart.total = parseFloat(activeItems.reduce((sum, item) => sum + (Number(item.nettotal) || 0), 0).toFixed(precision));
        updatedCart.tax = parseFloat(activeItems.reduce((sum, item) => sum + (Number(item.taxAmount) || 0), 0).toFixed(precision));
        updatedCart.discount = parseFloat(activeItems.reduce((sum, item) => sum + (Number(item.discount) || 0), 0).toFixed(precision));
        updatedCart.subTotal = parseFloat((updatedCart.total - updatedCart.tax).toFixed(precision));
      }
      updatedCart.paid = 0;
      updatedCart.payments = [];
      return updatedCart;
    });

    const array = [];
    await db.tableData
      .where("cwrFbTableId")
      .equals(tableRecord.cwrFbTableId)
      .toArray()
      .then(async (response) => {
        if (response.length > 0) {
          const orderedData = [];
          const cancelData = [];
          const cancelItems = [];
          const table = [...response];
          const cartItems = activeOrder?.cart?.items || table[0]?.cart?.items || [];

          cartItems.forEach((x) => {
            if (selectedTableRecord.splitTable && x.splitTable && x.splitTable !== selectedTableRecord.splitTable) {
              return;
            }
            if (x.update === "N") {
              orderedData.push(x);
            }
            if (x.isCanceled === "Y") {
              cancelData.push(x);
              cancelItems.push(`{
                    fbOrderLineId:"${x.fbOrderLineId}"
                      isCanceled: "Y"
                  }`);
            }
          });

          if (cancelItems.length > 0) {
            const obj = {
              fbOrderId: activeOrder?.fbOrderId || table[0].fbOrderId || table[0].cart.fbOrderId,
              cancelItems: cancelItems,
            };
            SyncData(obj, "cancelItem");
          }

          setOederedData(orderedData);
          array.push(...response);

          if (selectedTableRecord?.statusType === "OPN" || selectedTableRecord?.statusType === "AVL") {
            if (inputRef.current) {
              inputRef.current.focus();
            }
            if (posConfig?.enableGuestRegistration === "Y") {
              setGuestsModalFlag(true);
            } else {
              setShowProducts(true);
            }
            setSelectedTable(selectedTableRecord);
          } else {
            selectedTableRecord.noOfPersons = selectedTableRecord.noOfPersons || array[0].noOfPersons;
            selectedTableRecord.waiter = array[0].waiter;
            selectedTableRecord.guestName = selectedTableRecord.guestName || array[0].guestName;
            selectedTableRecord.guestType = selectedTableRecord.guestType || array[0].guestType;
            selectedTableRecord.fbOrderId = activeOrder?.fbOrderId || array[0].fbOrderId;
            selectedTableRecord.items = orderedData;
            selectedTableRecord.fbtableStatusId = array[0].fbtableStatusId === undefined ? uuidv4().replace(/-/g, "").toUpperCase() : array[0].fbtableStatusId;

            useRestaurantStore.getState().setTableName(selectedTableRecord);
            setSelectedTable(selectedTableRecord);
            setShowProducts(true);

            setTableCards((prevTableCards) =>
              prevTableCards?.map((tableCard) => (tableCard.cwrFbTableId === tableRecord.cwrFbTableId ? { ...tableCard, ...tableRecord } : tableCard))
            );
          }
        }
      });
  };

  const handleDisplayOk = async (recordState, itemsToFire = []) => {
    if (isFiringRef.current) return;
    isFiringRef.current = true;
    try {
    setSelectedItemsModalOpen(false);
    setShowSendToKitchen(false);

    let uniqId = uuidv4().replace(/-/g, "").toUpperCase();

    if (recordState) {
      recordState.uniqId = uniqId;
    }

    useRestaurantStore.getState().setTableName(recordState);

    const seqType = resolveSequenceType(cart, useSessionStore);
    let fireDocumentNo;
    if (seqType === "preorder") {
      const preSeq = useSessionStore.getState().preorderSequence;
      fireDocumentNo = generateDocumentNo(preSeq);
    } else {
      fireDocumentNo = `${tillData.tillAccess.cwrTill.prefix}${useSessionStore.getState().documentSequence + 1}`;
    }
    // TSK-2935: every fire used to unconditionally mint a fresh sOrderID/documentno —
    // harmless pre-TSK-2935 since nothing existed server-side until final settlement, so
    // only the LAST fire's identity ever mattered. Prepare Bill now creates a real,
    // confirmed s_order row mid-session (posOrderProcessor1, preparedBillOrigin:"Y"). If
    // more items get fired afterward, minting a new identity here orphans that confirmed
    // order while preparedBillOrigin:"Y" survives the `...cart` spread below — the
    // classifier then routes final submission to confirmPOSLayawayOrder1 for an sOrderID
    // the backend never created (FK violation on s_ordertax at confirm time). Keep firing
    // under the existing confirmed identity instead of replacing it.
    const isConfirmedPreparedBill = cart?.preparedBillOrigin === "Y" && !!cart?.sOrderID;
    let newCart = {
      ...cart,
      tableNo: recordState?.table,
      ...(isConfirmedPreparedBill ? {} : { sOrderID: uuidv4().replace(/-/g, "").toUpperCase(), documentno: fireDocumentNo }),
    };
    newCart?.items?.forEach((cartItem) => {
      cartItem.parkedItem = true;
    });

    const pcTokenMap = {};
    const tokenKey = getDailyGlobalTokenKey();
    let currentTokenCounter = Number(localStorage.getItem(tokenKey)) || 0;

    const fireableLineIds = new Set((itemsToFire.length > 0 ? itemsToFire : pendingFireItems).map((i) => i.lineId));

    const finalizedItems = newCart.items.map((cartItem) => {
      if (cartItem.isHold) return cartItem;

      const shouldFire = fireableLineIds.has(cartItem.lineId);
      if (!shouldFire) return cartItem;

      const pcId = cartItem.productionCenterId;
      let kotTokenId = cartItem.kotTokenId;
      let kotTokenNo = cartItem.kotTokenNo;

      if (!kotTokenId && pcId) {
        if (!pcTokenMap[pcId]) {
          pcTokenMap[pcId] = uuidv4().replace(/-/g, "").toUpperCase();
        }
        kotTokenId = pcTokenMap[pcId];
      }

      if (shouldFire) {
        currentTokenCounter += 1;
        kotTokenNo = `T-${currentTokenCounter}`;
      }

      return {
        ...cartItem,
        order: "Y",
        update: "N",
        orderStatus: "OR",
        ...(kotTokenId ? { kotTokenId } : {}),
        ...(kotTokenNo ? { kotTokenNo } : {}),
      };
    });

    if (currentTokenCounter > 0) {
      localStorage.setItem(tokenKey, String(currentTokenCounter));
    }

    const allItemsFired = finalizedItems.every((item) => item.isHold || item.order === "Y");
    let orderLines = [];

    if (recordState?.cwrFbTableId) {
      // toArray + JS find: a .filter() closure capturing recordState is lost over
      // the SQLite IPC bridge, silently returning no match — the fire would then
      // mint a NEW fbOrder instead of appending to the table's active one.
      const tableFbOrders = await db.fbOrderData.where("cwrFbTableId").equals(recordState.cwrFbTableId).toArray();
      const existingFbOrder = tableFbOrders.find(
        (order) => order.fbOrderStatus === "IP" && (recordState.splitTable ? order.splitTable === recordState.splitTable : !order.splitTable)
      );

      let fbOrder;
      let fbOrderId;
      if (existingFbOrder) {
        fbOrder = existingFbOrder;
        fbOrderId = existingFbOrder.fbOrderId;
      } else {
        const fbOrders = JSON.parse(localStorage.getItem("getFBOrderData") || "[]");
        const filteredOrders = Array.isArray(fbOrders)
          ? fbOrders.filter((o) => {
              const tableMatch = o.fbTableId === recordState?.cwrFbTableId || o.fbTableId === recordState?.fbTableId;
              if (!tableMatch) return false;
              if (recordState?.splitTable) return o.splitTable === recordState.splitTable;
              return !o.splitTable;
            })
          : [];
        const fbOrderDataID = filteredOrders[0]?.fbOrderId;
        fbOrderId = fbOrderDataID || uuidv4().replace(/-/g, "").toUpperCase();
        fbOrder = {
          fbOrderId,
          cwrFbTableId: recordState?.cwrFbTableId,
          fbOrderStatus: "IP",
          lines: [],
          fbOrderSync: "N",
          cart: newCart,
          name: recordState?.table,
          noOfPersons: recordState?.noOfPersons,
          statusType: "OCU",
          dineIn: useRestaurantStore.getState().dineIn,
          uniqId: uniqId,
          splitTable: recordState?.splitTable || null,
        };
      }

      const isFireAll = fireMode === "all";
      const itemsForUpsert = finalizedItems.filter((i) => fireableLineIds.has(i.lineId) && !i.isHold);

      itemsForUpsert.forEach((item) => {
        const prevFbOrderLineId = item.fbOrderLineId; // [BUG-1601] server id this line already had, if any
        // [BUG-1601][FIX] Reuse the line's existing server fbOrderLineId on re-fire instead of
        // minting a new one. Confirmed from server data: the same cart line (lineId 8BA446…) was
        // sent under two fbOrderLineIds across fires, and the server — which keys lines by
        // fbOrderLineId and REPLACES qty on match — treated the re-fire as a NEW line. So
        // "add 5, fire, add 5, fire" produced two Thandai lines of qty 10 (=20 units): the
        // doubled bill + phantom Fired Items row. Keeping the id makes the re-fire update the same
        // server line to its new total. A genuinely new line has no prior id → still a fresh uuid.
        let fbOrderLineId = prevFbOrderLineId || uuidv4().replace(/-/g, "").toUpperCase();
        let fbMetaOrderId = uuidv4().replace(/-/g, "").toUpperCase();
        const lineId = item.lineId;
        const usedKeysLower = new Set([
          "productid",
          "mbatchid",
          "description",
          "uom",
          "tax",
          "discount",
          "weight",
          "unitprice",
          "listprice",
          "linegross",
          "lineid",
          "grossstd",
          "name",
          "value",
          "grossunit",
          "linestd",
          "netlist",
          "netstd",
          "netunit",
          "unittax",
          "order",
        ]);

        const metaFromItem = Object.fromEntries(Object.entries(item).filter(([key]) => !usedKeysLower.has(key.toLowerCase())));
        const metaString = { ...metaFromItem, lineId, fbMetaOrderId, fbOrderLineId, isSelected: "N", order: "Y" };

        item.fbOrderLineId = fbOrderLineId;
        // [BUG-1921] Record that THIS terminal just fired this fbOrderLineId, so the
        // ORDER_UPSERT merge (useWebSocketHandler.js) can tell a lagging echo of this
        // fire apart from a genuine external update (e.g. a Mobile App cancellation)
        // for the same line arriving shortly after.
        markLocalFire(fbOrderLineId);
        orderLines.push({
          fbOrderId: `${fbOrderId}`,
          fbOrderLineId: `${fbOrderLineId}`,
          lineId: lineId,
          mPoductId: `${item.productId || item.mProductId || item.mPoductId}`,
          mBatchId: item.mBatchId,
          description: `${item.description || ""}`,
          csUomId: `${item.uom || item.csUomId || item.uomData?.[0]?.csUomId}`,
          csTaxId: `${item.tax || item.cTaxId}`,
          discount: item.discount,
          line: 1,
          qty: item.weight,
          unitPrice: item.unitPrice,
          listPrice: item.listPrice,
          lineNet: item.linenet,
          lineTax: item.taxRate,
          lineGross: item.linegross,
          sOrderLineId: null,
          isOrdered: "Y",
          isCanceled: "N",
          grossStd: item.grossstd,
          grossUnit: item.grossunit,
          lineStd: item.linestd,
          netList: item.netList,
          netStd: item.netStd,
          netUnit: item.netunit,
          unitTax: item.unitTax,
          meta: [
            {
              key: "orderLine",
              value: JSON.stringify(metaString),
              fbOrderLineMetaId: `${fbMetaOrderId}`,
            },
          ],
        });
      });

      let array1 = [];
      for (let i = 0; i < cart.items.length; i++) {
        let item = cart.items[i];
        let printer = item.productionCenter;
        if (!array1.hasOwnProperty(`${printer}`)) {
          array1[`${printer}`] = [];
        }
        array1[`${printer}`].push(cart.items[i]);
      }

      const filteredCart = {
        ...newCart,
        items: (itemsToFire.length > 0 ? itemsToFire : pendingFireItems).filter((item) => !item.isHold),
      };

      newCart.items = finalizedItems.map((cartItem) => {
        if (cartItem.isHold) return cartItem;
        if (itemsToFire.length === 0) {
          return { ...cartItem, order: "Y", update: "N" };
        }
        const isFired = itemsToFire.some((i) => i.lineId === cartItem.lineId);
        return isFired ? { ...cartItem, order: "Y", update: "N" } : cartItem;
      });

      // Snapshot the pre-fire line history BEFORE overwriting fbOrder.lines —
      // fbOrder aliases existingFbOrder, so reading existingFbOrder.lines after
      // this assignment would just see the new delta (losing earlier batches).
      const previousLines = [...(fbOrder.lines || [])];
      fbOrder.lines = orderLines;
      fbOrder.fbOrderSync = "N";
      const preciseTotal = parseFloat(cart?.total?.toFixed(precision));
      newCart = {
        ...newCart,
        items: finalizedItems,
        totalStd: cart?.items?.reduce((sum, item) => (item?.isCanceled === "Y" ? sum : sum + (item?.linestd || 0)), 0) || 0,
        amountInWords: numberToWords(preciseTotal),
      };
      fbOrder.cart = newCart;
      setCart(newCart);
      if (recordState?.splitTable) {
        fbOrder.splitTable = recordState.splitTable;
      }

      // IMPORTANT CHANGE: Send to server FIRST, revert on failure.
      // Pass newCart explicitly — it is the finalized post-fire cart for THIS
      // table; SyncData must never fall back to the live store cart, which may
      // already belong to a different table the cashier switched to.
      const response = await SyncData(fbOrder, "upsertFbOrder", null, "", true, newCart);

      // Check server response
      const gqlStatus = response?.data?.data?.upsertFBOrder?.status;
      if (!response || gqlStatus !== "200") {
        // SERVER FAILED - revert everything, show error
        const revertedCart = {
          ...cart,
          items: cart.items.map((item) => {
            if (fireableLineIds.has(item.lineId)) {
              return {
                ...item,
                order: "N",
                orderStatus: undefined,
                kotTokenId: undefined,
                kotTokenNo: undefined,
                firedQty: Math.max(0, (item.firedQty || 0) - (item.sendQty || 0)),
              };
            }
            return item;
          }),
        };
        setCart(revertedCart);

        // Roll back the token counter
        localStorage.setItem(tokenKey, String(currentTokenCounter - itemsForUpsert.length));

        message.error({
          content: "Failed to send order to kitchen. Please try again.",
          duration: 4,
        });
        return;
      }

      // SERVER SUCCESS - now write to local DB.
      // get() + update() with a computed object instead of .modify(closure): the
      // closure captures recordState/finalizedItems/newCart, which are LOST over
      // the SQLite IPC bridge (function toString serialization) — in CW Browser
      // the fired items were never written to tableData, so reopening the table
      // restored the pre-fire snapshot and fired items vanished from the order.
      const tableRow = await db.tableData.get(recordState?.cwrFbTableId);
      if (tableRow) {
        const tableUpdates = {
          totalAmount: newCart.total,
          documentno: newCart.documentno,
          time: dayjs(new Date()).format(userPreferences.dateTimeFormat),
          tableSync: "N",
          statusType: "ONG",
        };
        if (recordState?.splitTable && Array.isArray(tableRow.splitTables)) {
          console.debug("[v0] Updating split table items for:", recordState.splitTable);
          tableUpdates.splitTables = tableRow.splitTables.map((split) =>
            split.splitTable === recordState.splitTable
              ? {
                  ...split,
                  status: "ONG",
                  cart: { ...newCart, items: finalizedItems.map((item) => ({ ...item, splitTable: recordState.splitTable })) },
                }
              : split
          );
          tableUpdates.items = [];
        } else {
          tableUpdates.items = finalizedItems;
          // Keep the table's wsData snapshot in step with the fire — the fire path
          // previously never wrote wsData, so a stale snapshot from an earlier WS
          // event won on table reopen and erased the just-fired items from the cart.
          tableUpdates.wsData = { ...(tableRow.wsData || {}), ...newCart, items: finalizedItems };
        }
        await db.tableData.update(recordState.cwrFbTableId, tableUpdates);
      }

      // Persist the FULL line history locally. fbOrder.lines holds only the
      // just-fired delta (that is what the server call must send — it keys lines
      // by fbOrderLineId and replaces qty on match), but storing the delta alone
      // dropped every previously fired line from the local record, so items from
      // earlier KOT batches disappeared from the order and the bill.
      // previousLines was snapshotted before fbOrder.lines was overwritten.
      const mergedLines = [
        ...previousLines.filter((pl) => !orderLines.some((nl) => nl.fbOrderLineId === pl.fbOrderLineId)),
        ...orderLines,
      ];
      await db.fbOrderData.put({ ...fbOrder, lines: mergedLines, fbOrderSync: "Y" }, fbOrderId);

      // Rest of the success block
      const documentNo = response?.data?.data?.upsertFBOrder?.documentNo;
      const orderId = documentNo || localStorage.getItem("orderId");

      const updatedTableData = await db.tableData.toArray();
      const updatedTable = updatedTableData.find((t) => t.cwrFbTableId === recordState?.cwrFbTableId);

      if (updatedTable) {
        await SyncData(updatedTable, "upsertTableStatus");
      }
      setTableCards(updatedTableData);
      const ocuTables = updatedTableData.filter((table) => table.statusType === "OCU" || table.statusType === "ONG");
      setOcupiedTablesData(ocuTables);
      const resTables = updatedTableData.filter((table) => table.statusType === "RES");
      setReservedTablesData(resTables);
      let tempData = [];
      await db.tableData.toArray().then((fetched) => {
        tempData.push(...fetched);
      });
      setTableList(tempData);

      const tableOccupied = newCart?.statusType === "OCU";
      if (!tableOccupied) {
        const fireSeqType = resolveSequenceType(newCart, useSessionStore);
        if (fireSeqType === "preorder") {
          useSessionStore.getState().incrementPreorderSequence();
        } else {
          const newDocumentSequence = documentSequence + 1;
          useSessionStore.getState().setDocumentSequence(newDocumentSequence);
          setDocumnetSequence(newDocumentSequence);
        }
      }

      if (cart.saleType.value !== "TA") {
        setKeyValue("0");
      }
      if (allItemsFired) {
        setShowProducts(false);
      }
      if (posConfig?.enableKOTPrint === "Y") {
        if (CWHardwareController) {
          const finalFilteredCart = { ...filteredCart, orderId };
          CWKOTPrinter(finalFilteredCart);
        } else {
          kotPrinter(array1, fbOrder.fbOrderId);
        }
      }
    }
    } finally {
      // Always released, including the "server failed" early return above and
      // any unexpected throw — an unreleased guard would permanently block
      // every future fire for the rest of the session, which is worse than
      // the bug this fixes.
      isFiringRef.current = false;
    }
  };

  const handleCancelOrder = async () => {
    await db.tableData
      .where("cwrFbTableId")
      .equals(selectedTable.cwrFbTableId)
      .toArray()
      .then(async (response) => {
        if (response.length > 0) {
          response[0].status = "OPN";
          response[0].salesRepId = response[0].waiter;
          response[0].lines = [];
          response[0].color = "rgba(146, 144, 152, 0.5)";
          response[0].statusType = "OPN";
          response[0].fbOrderSync = "N";
          response[0].tableSync = "N";
          response[0].guestName = "";
          response[0].noOfPersons = 0;
          response[0].guestType = "";
          response[0].waiter = null;
          await db.tableData.update(response[0].cwrFbTableId, response[0]);
          SyncData(response[0], "upsertTableStatus");
          await db.fbOrderData
            .where("cwrFbTableId")
            .equals(selectedTable?.cwrFbTableId)
            .toArray()
            .then((ordersFetched) => {
              if (ordersFetched.length > 0) {
                ordersFetched.map(async (fbOrder) => {
                  if (fbOrder.fbOrderStatus === "IP") {
                    // Mutate the object too, not just the DB row: syncing the stale
                    // in-memory "IP" status leaves the order open server-side AND
                    // regenerates KOTs for every line (skipKots only applies to non-IP).
                    fbOrder.fbOrderStatus = "CL";
                    fbOrder.fbOrderSync = "N";
                    await db.fbOrderData.where("fbOrderId").equals(fbOrder.fbOrderId).modify({ fbOrderSync: "N", fbOrderStatus: "CL" });
                    await SyncData(fbOrder, "upsertFbOrder");
                  }
                });
              }
            });
          const updatedTableData = await db.tableData.toArray();
          setTableCards(updatedTableData);
          const ocuTables = updatedTableData.filter((table) => table.statusType === "OCU" || table.statusType === "ONG");
          setOcupiedTablesData(ocuTables);
          const resTables = updatedTableData.filter((table) => table.statusType === "RES");
          setReservedTablesData(resTables);
          let tempData = [];
          await db.tableData.toArray().then((fetched) => {
            tempData.push(...fetched);
          });
          setTableList(tempData);
          history.push("/pos");
        }
      });
  };

  return {
    getDailyGlobalTokenKey,
    handleNewOrder,
    handleExistingOrder,
    cleanupCompletedOrders,
    handleOrder,
    handleTable,
    handleDisplayOk,
    handleCancelOrder,
  };
};

export default useOrderHandlers;
